import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createExternalUser } from "../../api/ExternalUsers";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import { getAllOrganizations } from "../../api/Resources";
import ExternalUserForm from "../../components/externalUser/ExternalUserForm";
import {
    mapOrganizationsToOptions,
    shouldExternalUserBelongToOrganization,
} from "../../components/externalUser/externalUserUtils";
import { useTranslation } from "react-i18next";

const CreateExternalUserPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [orgLoading, setOrgLoading] = useState(false);

    const [user, setUser] = useState({
        firstName: "",
        lastName: "",
        email: "",
        message: t("form.defaultInviteMessage"),
        externalUserRole: "",
        organizationPublicId: null,
    });

    useEffect(() => {
        const loadOrganizations = async () => {
            setOrgLoading(true);
            try {
                const token = await getAccessToken();
                const orgs = await getAllOrganizations(token);
                setOrganizations(mapOrganizationsToOptions(orgs));
            } catch (err) {
                console.error(err);
                toast.error(t("toast.failedLoad", { entity: t("entity.organizations") }));
            } finally {
                setOrgLoading(false);
            }
        };

        loadOrganizations();
    }, [getAccessToken]);

    const handleInputChange = (field, value) => {
        setHasChanges(true);
        setUser((prev) => ({ ...prev, [field]: value }));
        setInvalidFields((prev) => prev.filter((item) => item !== field));
    };

    const handleCreateUser = async () => {
        const toastId = toast.loading(t("toast.creating", { entity: t("entity.user") }));
        setLoading(true);

        const requiredFields = ["firstName", "lastName", "email", "externalUserRole"];
        if (shouldExternalUserBelongToOrganization(user.externalUserRole)) {
            requiredFields.push("organizationPublicId");
        }

        const missingFields = requiredFields.filter((field) => {
            const value = user[field];
            return value === "" || value === undefined || value === null;
        });

        setInvalidFields(missingFields);

        if (missingFields.length > 0) {
            toast.error(t("common.pleaseFillRequired"), { id: toastId });
            setLoading(false);
            return;
        }

        try {
            const accessToken = await getAccessToken();
            const payload = {
                ...user,
                organizationPublicId: shouldExternalUserBelongToOrganization(
                    user.externalUserRole
                )
                    ? user.organizationPublicId
                    : null,
            };

            const newUser = await createExternalUser(payload, accessToken);

            toast.success(t("toast.created", { entity: t("entity.user") }), { id: toastId });
            navigate(`/ExternalUser/${newUser.externalUserPublicId}`);
        } catch (error) {
            toast.error(error?.message || t("toast.failedCreate", { entity: t("entity.user") }), { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    const navigateBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/externalUser");
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("entity.externalUsers")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={navigateBack} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4">
                                {t("pages.createExternalUser")}
                            </h2>

                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={navigateBack}>
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges || loading}
                                    onClick={handleCreateUser}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <ExternalUserForm
                                user={user}
                                invalidFields={invalidFields}
                                organizationOptions={organizations}
                                orgLoading={orgLoading}
                                onChange={handleInputChange}
                                editableFields={{
                                    firstName: true,
                                    lastName: true,
                                    email: true,
                                    externalUserRole: true,
                                    organizationPublicId: true,
                                }}
                                title=""
                            />
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default CreateExternalUserPage;
